import { useCallback } from 'react'

import {
  CustomFieldSchema,
  CustomFieldType,
  DateTypeOptions,
  Post,
  PostMappingTypeEnum,
  PostStatus,
  PostType,
  Space,
} from '@tribeplatform/gql-client/types'
import {
  useUpdatePost,
  useAddPost,
  useCreateDraftPost,
} from '@tribeplatform/react-sdk/hooks'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { convertTzToUtc, formatUtcDateTime } from '../common/utils/date.js'
import { CustomFieldSubtype, getFieldSetting } from '../CustomField/utils.js'
import { FormMethods } from '../Form/index.js'
import { useI18n } from '../i18n/index.js'
import { PostFormFields } from './types.js'

export interface SubmitPostProps {
  formData: PostFormFields
  methods: FormMethods<PostFormFields>
  type: 'draft' | 'publish'
}

interface Props {
  targetSpace: Space
  canSetOwnerId: boolean
  canSetPublishedAt: boolean
  fields: CustomFieldSchema[]
  setEnablePrompt: (enable: boolean) => void
  isNew: boolean
  postType: PostType
  post: Post
  onFormSubmit: (post: Post) => void
}

export const useSubmitPost = ({
  targetSpace,
  canSetOwnerId,
  canSetPublishedAt,
  fields,
  setEnablePrompt,
  isNew,
  postType,
  post,
  onFormSubmit,
}: Props) => {
  const { $t } = useI18n()
  const { mutateAsync: addPost, isLoading: isAdding } = useAddPost({
    fields: 'basic',
  })
  const { mutateAsync: updatePost, isLoading: isUpdating } = useUpdatePost({
    fields: 'basic',
  })
  const { mutateAsync: createDraftPost, isLoading: isCreatingDraft } =
    useCreateDraftPost({
      fields: 'basic',
    })

  const createInputPayload = useCallback(
    (formData: PostFormFields, methods: FormMethods<PostFormFields>) => {
      const mappingFields = []
      const tagNames = formData?.tags?.map(tag => tag?.title) || []
      const spaceId = targetSpace?.id
      const ownerId =
        formData?.owner?.id && canSetOwnerId ? formData?.owner?.id : undefined

      const publish =
        typeof formData?.publish === 'undefined' ? true : formData.publish

      const { isDirty: isPublishedAtDirty } =
        methods.getFieldState('publishedAt')
      const isPublishedProvided = !!formData?.publishedAt
      const shouldSubmitPublishedAt =
        canSetPublishedAt && isPublishedAtDirty && isPublishedProvided

      const publishedAt = shouldSubmitPublishedAt
        ? convertTzToUtc(formData?.publishedAt)
        : undefined

      if (!spaceId) {
        methods.setError(`targetSpace`, {
          message: $t({
            id: 'Post.Error.NoSpaceSelected',
            defaultMessage: 'Please select a {SPACE}',
          }),
          type: 'validate',
        })

        return {
          postInput: null,
          spaceId: null,
          mappingFields: [],
        }
      }

      Object.keys(formData.fields).forEach(key => {
        let value = formData.fields[key] ?? null

        const field = fields?.find(field => field.key === key)
        if (field?.type === CustomFieldType.relation) {
          value = value?.id
            ? {
                id: value?.id,
                relation: field?.typeOptions?.relationType,
              }
            : null
        } else if (
          field?.type === CustomFieldType.array &&
          field?.items?.type === CustomFieldType.relation
        ) {
          value =
            value?.map(item => ({
              id: item?.id,
              relation: field?.items?.typeOptions?.relationType,
            })) || []
        } else if (field?.type === CustomFieldType.date) {
          if (value === '') {
            value = null
          }

          if (value) {
            const subtype = getFieldSetting(
              field,
              'subtype',
            ) as CustomFieldSubtype
            if (subtype === CustomFieldSubtype.DATETIME) {
              value = convertTzToUtc(value)
            } else {
              const dateType = field?.typeOptions?.dateType
              if (dateType === DateTypeOptions.datetime) {
                value = formatUtcDateTime(value)
              } else {
                value = formatUtcDateTime(value, 'YYYY-MM-DD')
              }
            }
          }
        } else if (
          field?.type === CustomFieldType.text ||
          field?.type === CustomFieldType.richText
        ) {
          if (value === '') {
            value = null
          }
        } else if (field?.type === CustomFieldType.number) {
          if (value === '') {
            value = null
          } else {
            const numberValue = Number(value)
            if (!Number.isNaN(numberValue)) {
              value = numberValue
            } else {
              value = null
            }
          }
        }

        if (typeof value !== 'undefined') {
          mappingFields.push({
            key,
            value: JSON.stringify(value),
            type: PostMappingTypeEnum.text,
          })
        }
      })

      const postInput = {
        mappingFields,
        tagNames,
        ownerId,
        publish,
        publishedAt,
        locked: formData.locked,
        scheduledFor: formData.scheduledFor,
        slug: formData.slug,
        customSeoDetail: formData.customSeoDetail
          ? {
              thumbnailId: formData.customSeoDetail?.thumbnail?.id ?? null,
              noIndex: formData.customSeoDetail?.noIndex,
              description: formData.customSeoDetail?.description,
              title: formData.customSeoDetail?.title,
              canonicalUrl: formData.customSeoDetail?.canonicalUrl || null,
            }
          : undefined,
      }

      return { postInput, spaceId, mappingFields }
    },
    [targetSpace?.id, canSetOwnerId, canSetPublishedAt, $t, fields],
  )

  const onSubmit = useCallback(
    async ({ formData, methods, type }: SubmitPostProps) => {
      const { postInput, spaceId, mappingFields } = createInputPayload(
        formData,
        methods,
      )

      if (!postInput || !spaceId) {
        return
      }

      try {
        setEnablePrompt(false)
        let newPost
        const publish = !postInput.scheduledFor && type !== 'draft'
        if (!isNew) {
          newPost = await updatePost({
            id: post?.id,
            input: {
              ...postInput,
              publish,
            },
          })
        } else if (type === 'draft') {
          newPost = await createDraftPost({
            spaceId,
            input: {
              ...postInput,
              postTypeId: postType?.id,
            },
          })
        } else {
          newPost = await addPost({
            spaceId,
            input: {
              ...postInput,
              postTypeId: postType?.id,
            },
          })
        }

        if (newPost.status === PostStatus.BLOCKED) {
          toast({
            title: $t({
              id: 'Generics.PendingReview',
              defaultMessage: 'Pending review',
            }),
            description: $t({
              id: 'Post.PendingReview.Description',
              defaultMessage:
                'Your post will be visible to others once it’s been reviewed by a moderator.',
            }),
            status: 'info',
          })
        }

        onFormSubmit(newPost)
      } catch (error) {
        const errors =
          error?.response?.errors.filter(error => error?.field) || []
        if (errors.length > 0) {
          errors.forEach(error => {
            // If it's a field error, add `fields.` prefix to the field name
            const findErrorFieldName = (errorField: string) => {
              if (fields.find(field => field?.key === errorField)) {
                return `fields.${errorField}`
              }

              const field = mappingFields.find(
                (field, index) => errorField === `mappingFields.${index}.value`,
              )
              if (field) {
                return `fields.${field.key}`
              }

              return errorField
            }

            const errorField = findErrorFieldName(error.field)

            methods.setError(errorField, {
              message: error?.message,
              type: 'validate',
            })
          })
        } else {
          const message = error?.response?.errors
            ?.map(e => e.message)
            ?.join('\n')
          toast({
            title: $t({
              id: 'Generics.Error',
              defaultMessage: 'Error',
            }),
            description:
              message ||
              $t({
                id: 'Generics.SomethingWentWrongTryAgain',
                defaultMessage: 'Something went wrong, please try again.',
              }),
            status: 'error',
          })
        }
      }
    },
    [
      createInputPayload,
      setEnablePrompt,
      isNew,
      onFormSubmit,
      updatePost,
      post?.id,
      createDraftPost,
      postType?.id,
      addPost,
      $t,
      fields,
    ],
  )

  return { onSubmit, isAdding: isAdding || isCreatingDraft, isUpdating }
}
